import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Container, ListGroup, InputGroup, Input, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faStar as SolidStar, faThumbtack as Pin } from '@fortawesome/free-solid-svg-icons';
import { faStar as RegularStar } from '@fortawesome/free-regular-svg-icons';

import AppContext from '../AppContext';
import dataManager from '../DataManager';
import InfoItem from './InfoItem';

import contentViewStyles from './ContentView.module.css';

function SearchView() {
    const { t } = useContext(AppContext);
    // const [unsubscribeSearch, setUnsubscribeSearch] = useState(null); // useState causes re-render and update slowly
    const unsubscribeSearch = useRef(null); // useRef doesn't cause re-render and update timely
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [expandedRecord, setExpandedRecord] = useState(null);
    const [editingTitleId, setEditingTitleId] = useState(null);
    const [editableTitle, setEditableTitle] = useState('');
    const [showNoResult, setShowNoResult] = useState(false);
    const [showStarredOnly, setShowStarredOnly] = useState(false);
    const [showPinnedOnly, setShowPinnedOnly] = useState(false);
    const [isLoading, setLoading] = useState(false);

    const toggleStarFilter = () => {
        setShowStarredOnly(!showStarredOnly);
    }

    const togglePinFilter = () => {
        setShowPinnedOnly(!showPinnedOnly);
    }

    const updateSearchResults = useCallback((results) => {
        if (results.length === 0) {
            setShowNoResult(true);
        } else {
            setShowNoResult(false);
            results.sort((a, b) => b.data.updated - a.data.updated);
        }
        setSearchResults(results);
        setLoading(false);
    }, []);

    const doSearch = useCallback(async () => {
        const keyword = searchText.trim().toLowerCase();
        if (!keyword) {
            return;
        }

        if (unsubscribeSearch.current) {
            unsubscribeSearch.current();
        }
 
        setLoading(true);
        const unsubscribe = dataManager.search(keyword, (results) => {
            const filteredResults = results.filter(record => (!showStarredOnly || record.data.starred) && (!showPinnedOnly || record.data.pinned));
            setTimeout(() => { updateSearchResults(filteredResults); }, 0); // put separate event loop to show loading spinner
        });
        unsubscribeSearch.current = unsubscribe;
    }, [searchText, showStarredOnly, showPinnedOnly, updateSearchResults, unsubscribeSearch]);

    const onChangeHandler = (e) => {
        setSearchText(e.target.value);
        setSearchResults([]);
        setShowNoResult(false);
    };

    useEffect(() => {
        doSearch();
    }, [showStarredOnly, showPinnedOnly, doSearch]);

    return (
        <Container className={contentViewStyles.pageContent}>
            <div className="d-flex justify-content-between align-items-center">
                <h1 className={`my-4 ${contentViewStyles.title}`} >{t('title.search')} {((searchResults.length > 0) || showNoResult) ? ` (${searchResults.length})` : ''}</h1>
                <div className="d-flex justify-content-end mb-2">
                    <div className={contentViewStyles[showStarredOnly ? 'iconWrapper' : 'disabledIconWrapper']} onClick={toggleStarFilter}>
                        <FontAwesomeIcon className={contentViewStyles.commandIcon}
                            icon={showStarredOnly ? SolidStar : RegularStar} title={t('command.show' + (showStarredOnly ? "All" : "StarredOnly"))}
                        />
                    </div>
                    <div className={contentViewStyles[showPinnedOnly ? 'iconWrapper' : 'disabledIconWrapper']} onClick={togglePinFilter}>
                        <FontAwesomeIcon className={contentViewStyles.commandIcon}
                            icon={Pin} title={t('command.show' + (showPinnedOnly ? "All" : "PinnedOnly"))}
                        />
                    </div>
                </div>
            </div>
            <InputGroup className="mb-3">
                <Input
                    placeholder={`${t('placeholder.search')}`}
                    value={searchText}
                    onChange={onChangeHandler}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            doSearch();
                        }
                    }}
                />
                <Button color="secondary" onClick={doSearch}>
                    <FontAwesomeIcon icon={faSearch} />
                </Button>
            </InputGroup>
            {showNoResult && <p>No results</p>}
            {isLoading && (
                <div className="d-flex justify-content-center">
                    <Spinner />
                </div>
            )}
            <ListGroup>
                {searchResults.map((record) => (
                    <InfoItem
                        key={record.id}
                        record={record}
                        states={{
                            expandedRecord, setExpandedRecord,
                            editingTitleId, setEditingTitleId,
                            editableTitle, setEditableTitle,
                        }}
                    />
                ))}
            </ListGroup>
        </Container>
    );
}

export default SearchView;
